import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiPatch } from '../../../api/client';
import type { Client } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { InfoSection, InfoRow } from '../../../components/ui/InfoCard';
import { formatDate } from '../../../utils/format';

export default function ClientOverviewTab({ client }: { client: Client }) {
  const qc = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Partial<Client>>({});

  const updateMutation = useMutation({
    mutationFn: (body: Partial<Client>) => apiPatch<Client>(`/clients/${client.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', client.id] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      setEditMode(false);
      toast.success('Cliente aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleEdit = () => {
    setForm({
      firstName:  client.firstName,
      lastName:   client.lastName,
      email:      client.email,
      phone:      client.phone ?? '',
      address:    client.address ?? '',
      city:       client.city ?? '',
      birthDate:  client.birthDate ?? '',
      birthPlace: client.birthPlace ?? '',
      fiscalCode: client.fiscalCode ?? '',
      vatNumber:  client.vatNumber ?? '',
      notes:      client.notes ?? '',
    });
    setEditMode(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(form);
  };

  if (editMode) {
    return (
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.5rem',
      }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Nome *" value={form.firstName ?? ''} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required />
            <Input label="Cognome *" value={form.lastName ?? ''} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required />
          </div>
          <Input label="Email" type="email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input label="Numero Cellulare" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input label="Indirizzo" value={form.address ?? ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
          <Input label="Città" value={form.city ?? ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          <Input label="Data di Nascita" type="date" value={form.birthDate ? form.birthDate.split('T')[0] : ''} onChange={e => setForm(f => ({ ...f, birthDate: e.target.value }))} />
          <Input label="Luogo di Nascita" value={form.birthPlace ?? ''} onChange={e => setForm(f => ({ ...f, birthPlace: e.target.value }))} />
          <Input label="Codice fiscale" value={form.fiscalCode ?? ''} onChange={e => setForm(f => ({ ...f, fiscalCode: e.target.value }))} />
          <Input label="P. IVA" value={form.vatNumber ?? ''} onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))} />
          <div>
            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)', display: 'block', marginBottom: '0.375rem' }}>Note</label>
            <textarea
              value={form.notes ?? ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={4}
              style={{
                width: '100%',
                background: 'var(--glass-bg-card)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                color: 'var(--color-text)',
                fontSize: 'var(--text-sm)',
                padding: '0.625rem 0.75rem',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: 1.6,
              }}
              placeholder="Aggiungi note sul cliente…"
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
            <Button variant="ghost" type="button" onClick={() => setEditMode(false)}>Annulla</Button>
            <Button type="submit" loading={updateMutation.isPending}>Salva modifiche</Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Anagrafica</span>
          <Button variant="ghost" size="sm" onClick={handleEdit}>Modifica</Button>
        </div>

        <InfoSection title="">
          <InfoRow label="Email"           value={client.email} />
          <InfoRow label="Numero Cellulare" value={client.phone} />
          <InfoRow label="Indirizzo"       value={client.address} />
          <InfoRow label="Città"           value={client.city} />
          <InfoRow label="Data di Nascita" value={client.birthDate ? new Date(client.birthDate).toLocaleDateString('it-IT') : undefined} />
          <InfoRow label="Luogo di Nascita" value={client.birthPlace} />
          <InfoRow label="Codice fiscale"  value={client.fiscalCode} />
          <InfoRow label="P. IVA"          value={client.vatNumber} />
          <InfoRow label="Cliente dal"     value={formatDate(client.createdAt)} />
          <InfoRow label="Stato"           value={client.active === false ? 'Archiviato' : 'Attivo'} />
        </InfoSection>
      </div>

      {client.notes && (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          padding: '1.25rem',
        }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Note</p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{client.notes}</p>
        </div>
      )}
    </div>
  );
}
